import { useMemo } from "react";
import { useCalendarFeed } from "../../lib/queries";
import { projectGradientCss } from "../../lib/projectColor";
import type { CalendarFeedItem } from "../../types/calendar";

// Top-of-rail hero card. Aero treatment: holographic-italic date hero,
// three-up summary in eyebrow font, mini event list with project pills.
export default function TodayRailPane() {
  const { data: feed = [] } = useCalendarFeed(14);

  const today = new Date();
  const dayStr = today.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  const stats = useMemo(() => {
    const startOfDay = new Date(today);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    const todayItems = feed.filter((f) => {
      const t = new Date(f.scheduled_at).getTime();
      return t >= startOfDay.getTime() && t <= endOfDay.getTime();
    });
    let events = 0;
    let bills = 0;
    let deadlines = 0;
    for (const f of todayItems) {
      if (f.feed_kind === "service_renewal" || f.category === "bill" || f.category === "renewal")
        bills++;
      else if (f.category === "deadline") deadlines++;
      else events++;
    }
    return { events, bills, deadlines };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed]);

  const upcoming = useMemo(() => {
    const future = feed.filter((f) => new Date(f.scheduled_at).getTime() >= Date.now());
    const firstOf = (pred: (f: CalendarFeedItem) => boolean) => future.find(pred);
    const event = firstOf(
      (f) =>
        f.feed_kind === "event" &&
        f.category !== "bill" &&
        f.category !== "renewal" &&
        f.category !== "deadline",
    );
    const bill = firstOf(
      (f) =>
        f.feed_kind === "service_renewal" || f.category === "bill" || f.category === "renewal",
    );
    const deadline = firstOf((f) => f.category === "deadline");
    const out = [event, bill, deadline].filter(Boolean) as CalendarFeedItem[];
    const seen = new Set(out.map((o) => o.feed_id));
    for (const f of future) {
      if (out.length >= 3) break;
      if (!seen.has(f.feed_id)) {
        out.push(f);
        seen.add(f.feed_id);
      }
    }
    return out.slice(0, 3);
  }, [feed]);

  return (
    <div className="aero-glass aero-glass-strong mx-1 px-4 py-3" style={{ borderRadius: 18 }}>
      <span
        className="font-eyebrow"
        style={{
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "var(--gold)",
          textShadow: "0 0 8px rgba(255,210,139,.55)",
        }}
      >
        ✦ Today
      </span>
      <h3
        className="mt-1 truncate text-2xl"
        style={{
          fontFamily: "Fraunces, serif",
          fontStyle: "italic",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          lineHeight: 1,
        }}
      >
        <span className="holo-text">{dayStr}</span>
      </h3>
      <div className="mt-2 flex flex-col gap-1">
        {upcoming.length === 0 ? (
          <span
            className="text-xs"
            style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", color: "var(--plum)", opacity: 0.7 }}
          >
            Nothing on the books — rare.
          </span>
        ) : (
          upcoming.map((u) => <RailItem key={u.feed_id} item={u} />)
        )}
      </div>
      <div
        className="mt-2 flex items-center gap-2 border-t pt-1.5"
        style={{ borderColor: "rgba(255,31,165,0.15)" }}
      >
        <span
          className="font-eyebrow"
          style={{
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--hot-pink)",
          }}
        >
          {stats.events}
        </span>
        <span style={{ color: "var(--plum)", opacity: 0.5, fontSize: 10 }}>events</span>
        <span style={{ color: "var(--plum)", opacity: 0.3 }}>·</span>
        <span
          className="font-eyebrow"
          style={{
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--gold)",
          }}
        >
          {stats.bills}
        </span>
        <span style={{ color: "var(--plum)", opacity: 0.5, fontSize: 10 }}>bills</span>
        <span style={{ color: "var(--plum)", opacity: 0.3 }}>·</span>
        <span
          className="font-eyebrow"
          style={{
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--lavender-deep)",
          }}
        >
          {stats.deadlines}
        </span>
        <span style={{ color: "var(--plum)", opacity: 0.5, fontSize: 10 }}>deadlines</span>
      </div>
    </div>
  );
}

function RailItem({ item }: { item: CalendarFeedItem }) {
  const grad = projectGradientCss(item.project, 135);
  const when = new Date(item.scheduled_at);
  const isToday = when.toDateString() === new Date().toDateString();
  const label = isToday
    ? when.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : when.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return (
    <div className="flex items-center gap-2">
      <span
        className="glossy-project-pill"
        style={{ background: grad, fontSize: 7, padding: "2px 6px" }}
      >
        ★
      </span>
      <span
        className="flex-1 truncate text-xs"
        style={{ color: "var(--ink)", fontWeight: 600 }}
      >
        {item.title}
      </span>
      <span
        className="font-eyebrow"
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.1em",
          color: "var(--hot-pink)",
        }}
      >
        {label}
      </span>
    </div>
  );
}
